import { useParams, useNavigate } from "react-router-dom";
import GitFitGridLoader from "@/components/GitFitGridLoader";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Activity, Lock, Copy, Users, Trophy, Medal, Award, Settings, Eye, EyeOff, Sparkles, UserPlus, UserCheck, UserMinus, Dices } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import ContributionChart from "@/components/ContributionChart";
import { AvatarUpload } from "@/components/AvatarUpload";
import { AppLogo } from "@/components/AppLogo";
import { BlockReportButton } from "@/components/BlockReportButton";
import { FollowersFollowingDialog } from "@/components/FollowersFollowingDialog";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatNumber, formatCurrency } from "@/lib/utils";
import { ENABLE_COMMITMENT_PLEDGES } from "@/config/features";
import { useAppSession } from "@/providers/AppSessionProvider";
import { useEffectiveUserId } from "@/hooks/useEffectiveUserId";
import { isDemoAccount } from "@/lib/screenshotDataGenerator";
import { trackStoreClick } from "@/lib/onboardingAnalytics";

const APP_STORE_URL = "https://apps.apple.com/us/app/gitfit-ai/id6754495386";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=ai.gitfit.app&pcampaignid=web_share";

const Profile = () => {
  const { t } = useTranslation();
  const { username } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showVisibilityDialog, setShowVisibilityDialog] = useState(false);
  const [showFollowersDialog, setShowFollowersDialog] = useState(false);
  const [followersDialogTab, setFollowersDialogTab] = useState<"followers" | "following">("followers");
  const [showDiceButtons, setShowDiceButtons] = useState(false);
  const isMobile = useIsMobile();

  // Use centralized session provider instead of local auth check
  const { ready, session } = useAppSession();
  const currentUser = useEffectiveUserId();

  const { data: profile, isLoading, error: profileError } = useQuery({
    queryKey: ["profile", username],
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("*").eq("username", username).single();

      if (error) throw error;

      // Check if the user account is soft-deleted
      const { data: activeUserIds } = await (supabase as any).rpc('get_active_user_ids', {
        user_ids: [data.id]
      });

      // If user is soft-deleted, throw error to show "Profile doesn't exist"
      if (!activeUserIds || (activeUserIds as string[]).length === 0) {
        throw new Error("Profile doesn't exist");
      }

      return data;
    },
    enabled: !!username,
    staleTime: 30 * 1000, // Reduced to 30 seconds for more frequent updates
    gcTime: 2 * 60 * 1000, // Reduced cache time to 2 minutes
    retry: 1, // Only retry once on error
  });

  // Check if there's any blocking relationship (bidirectional)
  const { data: blockingStatus } = useQuery({
    queryKey: ["blocking-status", currentUser, profile?.id],
    queryFn: async () => {
      if (!currentUser || !profile?.id || currentUser === profile.id) {
        return { isBlocked: false, hasBlocked: false };
      }

      // Check if current user blocked this profile
      const { data: userBlockedProfile } = await supabase
        .from("blocked_users")
        .select("id")
        .eq("user_id", currentUser)
        .eq("blocked_user_id", profile.id)
        .maybeSingle();

      // Check if this profile blocked current user
      const { data: profileBlockedUser } = await supabase
        .from("blocked_users")
        .select("id")
        .eq("user_id", profile.id)
        .eq("blocked_user_id", currentUser)
        .maybeSingle();

      return {
        isBlocked: !!profileBlockedUser, // Profile owner blocked the viewer
        hasBlocked: !!userBlockedProfile, // Viewer blocked the profile owner
      };
    },
    enabled: !!currentUser && !!profile?.id,
  });

  // Track profile views for external profiles (including anonymous users)
  useEffect(() => {
    const trackProfileView = async () => {
      // Wait for profile data to load
      if (!profile?.id) {
        return;
      }

      // Don't track if viewing own profile (only for authenticated users)
      if (currentUser && currentUser === profile.id) {
        return;
      }

      // Don't track views if there's any blocking relationship (only for authenticated users)
      if (currentUser && (blockingStatus?.isBlocked || blockingStatus?.hasBlocked)) {
        return;
      }

      try {
        // Call the edge function to track the view with IP-based rate limiting
        const { data, error } = await supabase.functions.invoke('track-profile-view', {
          body: { profileUserId: profile.id }
        });

        if (error) {
          console.error("Failed to track profile view:", error);
        } else if (data?.viewCounted) {
          // Only invalidate if the view was actually counted
          queryClient.invalidateQueries({ queryKey: ["profile", username] });
        }
      } catch (error) {
        console.error("Failed to track profile view:", error);
        // Silently fail - view tracking shouldn't break the experience
      }
    };

    trackProfileView();
  }, [profile?.id, currentUser, username, queryClient, blockingStatus]);

  // Query for follow status
  const { data: followStatus } = useQuery({
    queryKey: ["follow-status", currentUser, profile?.id],
    queryFn: async () => {
      if (!currentUser || !profile?.id) return { isFollowing: false, hasRequested: false };

      // Check if already following
      const { data: followData } = await supabase
        .from("follows")
        .select("id")
        .eq("follower_id", currentUser)
        .eq("following_id", profile.id)
        .maybeSingle();

      // Check for pending follow request
      const { data: requestData } = await supabase
        .from("follow_requests")
        .select("id")
        .eq("requester_id", currentUser)
        .eq("target_user_id", profile.id)
        .eq("status", "pending")
        .maybeSingle();

      return {
        isFollowing: !!followData,
        hasRequested: !!requestData
      };
    },
    enabled: !!currentUser && !!profile?.id && currentUser !== profile?.id,
  });

  // Query for follower/following counts
  const { data: followCounts } = useQuery({
    queryKey: ["follow-counts", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return { followers: 0, following: 0 };

      const { data: followerData, error: followerError } = await supabase.rpc(
        "get_follower_count",
        { p_user_id: profile.id }
      );

      const { data: followingData, error: followingError } = await supabase.rpc(
        "get_following_count",
        { p_user_id: profile.id }
      );

      if (followerError || followingError) {
        console.error("Error fetching follow counts:", followerError || followingError);
        return { followers: 0, following: 0 };
      }

      return {
        followers: followerData || 0,
        following: followingData || 0
      };
    },
    enabled: !!profile?.id,
  });

  const { data: profileRank, isLoading: isLoadingProfileRank } = useQuery({
    queryKey: ["profile-rank", profile?.id],
    queryFn: async () => {
      if (!profile?.id || !profile?.is_premium || !profile?.is_public) return null;

      const leaderboards = [
        { view: "leaderboard_calories", emoji: "🔥", key: "calories" },
        { view: "leaderboard_protein", emoji: "🍗", key: "protein" },
        { view: "leaderboard_miles", emoji: "🏃", key: "miles" },
        { view: "leaderboard_fiber", emoji: "🥦", key: "fiber" },
        { view: "leaderboard_water", emoji: "💧", key: "water" },
        { view: "leaderboard_gym_time", emoji: "💪", key: "gym_time" },
        { view: "leaderboard_github_commits", emoji: "💻", key: "github_commits" },
        { view: "leaderboard_body_fat", emoji: "📊", key: "body_fat" },
        { view: "leaderboard_ate_healthy", emoji: "🥗", key: "ate_healthy" },
        { view: "leaderboard_low_sugars", emoji: "🚫", key: "low_sugars" },
        { view: "leaderboard_fasted", emoji: "🍽️", key: "fasted" },
      ];

      let bestRank = null;
      let bestMetric = null;

      for (const lb of leaderboards) {
        const { data } = await supabase
          .from(lb.view as any)
          .select("*")
          .eq("id", profile.id)
          .maybeSingle();

        if (data) {
          const leaderData = data as { rank?: number };
          if (leaderData.rank) {
            const rank = Number(leaderData.rank);
            if (!bestRank || rank < bestRank) {
              bestRank = rank;
              bestMetric = { emoji: lb.emoji, key: lb.key };
            }
          }
        }
      }

      return bestRank ? { rank: bestRank, metric: bestMetric } : null;
    },
    enabled: !!profile?.id && !!profile?.is_premium && !!profile?.is_public,
  });

  // Mutation to follow a user
  const followUser = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error("Profile not found");

      const { data, error } = await supabase.rpc("follow_user", {
        p_target_user_id: profile.id,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; status?: string; requestId?: string } | null;
      if (!result?.success) throw new Error(result?.error || t('profile.follow_failed'));

      return result;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["follow-status", currentUser, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ["follow-counts", profile?.id] });

      if (data.status === "following") {
        toast.success(t('profile.following_toast'));

        // Send push notification for new follow on public profile
        if (profile?.id && currentUser) {
          try {
            await supabase.functions.invoke('send-follow-notification', {
              body: {
                type: 'new_follow',
                targetUserId: profile.id,
                requesterId: currentUser,
              }
            });
          } catch (error) {
            console.error('Failed to send notification:', error);
            // Don't fail the whole operation if notification fails
          }
        }
      } else if (data.status === "pending" || data.status === "requested") {
        toast.success(t('profile.request_sent_toast'));

        // Send push notification for follow request
        if (profile?.id && currentUser) {
          try {
            console.log('Sending follow request notification...', {
              type: 'follow_request',
              targetUserId: profile.id,
              requesterId: currentUser,
            });
            const result = await supabase.functions.invoke('send-follow-notification', {
              body: {
                type: 'follow_request',
                targetUserId: profile.id,
                requesterId: currentUser,
              }
            });
            console.log('Follow request notification result:', result);
          } catch (error) {
            console.error('Failed to send notification:', error);
            // Don't fail the whole operation if notification fails
          }
        }
      }
    },
    onError: (error: any) => {
      toast.error(error.message || t('profile.follow_failed'));
    },
  });

  // Mutation to unfollow a user
  const unfollowUser = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error("Profile not found");

      const { data, error } = await supabase.rpc("unfollow_user", {
        p_target_user_id: profile.id,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) throw new Error(result?.error || t('profile.unfollow_failed'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["follow-status", currentUser, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ["follow-counts", profile?.id] });
      toast.success(t('profile.unfollowed_toast'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('profile.unfollow_failed'));
    },
  });

  // Mutation to cancel follow request
  const cancelRequest = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error("Profile not found");

      const { data, error } = await supabase.rpc("cancel_follow_request", {
        p_target_user_id: profile.id,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) throw new Error(result?.error || t('profile.cancel_failed'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["follow-status", currentUser, profile?.id] });
      toast.success(t('profile.request_cancelled'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('profile.cancel_failed'));
    },
  });

  const getRankDisplay = (rankData: { rank: number; metric: { emoji: string; key: string } } | null) => {
    if (!rankData) return null;

    const { rank, metric } = rankData;
    let icon = null;
    if (rank === 1) icon = <Trophy className="w-4 h-4 text-yellow-500" />;
    else if (rank === 2) icon = <Medal className="w-4 h-4 text-gray-400" />;
    else if (rank === 3) icon = <Award className="w-4 h-4 text-amber-600" />;

    if (icon) {
      return (
        <div className="flex items-center gap-1">
          {icon}
          <span>
            {t('profile.worldwide', { rank, emoji: metric.emoji, label: t(`metrics.${metric.key}`) })}
          </span>
        </div>
      );
    }
    return (
      <span>
        {t('profile.worldwide', { rank, emoji: metric.emoji, label: t(`metrics.${metric.key}`) })}
      </span>
    );
  };

  const isOwnProfile = currentUser === profile?.id;
  const hasAccess = isOwnProfile || profile?.is_public || followStatus?.isFollowing;

  const updateVisibility = useMutation({
    mutationFn: async (isPublic: boolean) => {
      if (!profile?.id) throw new Error("Profile not found");
      const { error } = await supabase.from("profiles_internal").update({ is_public: isPublic }).eq("id", profile.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", username] });
      queryClient.invalidateQueries({ queryKey: ["profile-settings", profile?.id] });
      toast.success(t('profile.visibility_updated'));
      setShowVisibilityDialog(false);
    },
    onError: () => {
      toast.error(t('profile.visibility_failed'));
    },
  });

  const shareProfile = () => {
    if (profile?.username) {
      const shareUrl = `${window.location.origin}/${profile.username}`;
      navigator.clipboard.writeText(shareUrl);
      toast.success(t('profile.link_copied'));

      // Show visibility dialog if the account is private
      if (isOwnProfile && !profile.is_public) {
        setShowVisibilityDialog(true);
      }
    }
  };

  // Show loading while session is checking or profile is loading
  // Add timeout to prevent infinite loading on deep link resume
  if (ready === 'checking' || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <GitFitGridLoader className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  // Show "Profile doesn't exist" for soft-deleted users or missing profiles
  if (!profile || profileError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">{t('profile.not_found_title')}</h2>
          <p className="text-muted-foreground mb-4">{t('profile.not_found_desc')}</p>
          <Button onClick={() => navigate("/")}>{t('profile.go_home')}</Button>
        </Card>
      </div>
    );
  }

  // If there's any blocking relationship (either direction), show "Account doesn't exist"
  if (blockingStatus?.isBlocked || blockingStatus?.hasBlocked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">{t('profile.account_not_found_title')}</h2>
          <p className="text-muted-foreground mb-4">{t('profile.account_not_found_desc')}</p>
          <Button onClick={() => navigate("/")}>{t('profile.go_home')}</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-6xl mx-auto pt-16 pb-8 px-4">
        <div className="fixed top-4 left-4 z-50">
          <BackButton onClick={() => navigate(-1)} />
        </div>

        {/* Brand Logo - Top Left */}
        <div className="mb-6">
          <AppLogo isLoading={isLoading} href="https://gitfit.ai" />
        </div>

        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="max-w-full">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <AvatarUpload
                    userId={profile.id}
                    currentAvatarUrl={profile.avatar_url}
                    username={profile.username}
                    size="lg"
                    showUploadButton={false}
                    clickable={isOwnProfile}
                    viewable={!isOwnProfile}
                    uploadInputId="profile-avatar-upload"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl md:text-3xl font-bold truncate">@{isDemoAccount(profile.username) ? 'aaron' : profile.username}</h1>
                    {!isOwnProfile && currentUser && (
                      <BlockReportButton userId={profile.id} username={profile.username} />
                    )}
                    {profile.is_premium && (
                      <button
                        onClick={() => navigate("/upgrade")}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500/20 to-accent/20 border border-purple-500/30 flex-shrink-0 hover:from-purple-500/30 hover:to-accent/30 transition-colors cursor-pointer"
                      >
                        <Sparkles className="w-3 h-3 text-purple-500" />
                        <span className="text-xs font-semibold text-purple-500">{t('profile.pro')}</span>
                      </button>
                    )}
                    {isOwnProfile && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={shareProfile}
                        className="h-5 w-5 flex-shrink-0"
                      >
                        <Copy className="w-2 h-2 opacity-50" />
                      </Button>
                    )}
                    {(isOwnProfile || profile.is_public || followStatus?.isFollowing) && profile.profile_views !== undefined && (
                      <div className="inline-flex items-center gap-1 text-muted-foreground flex-shrink-0">
                        <Eye className="w-3 h-3" />
                        <span className="text-xs font-medium">{formatNumber(profile.profile_views)}</span>
                      </div>
                    )}
                  </div>
                </div>
                {!isOwnProfile && ENABLE_COMMITMENT_PLEDGES && Number(profile.donations_total) > 0 && (
                  <p className="text-[11px] font-medium text-muted-foreground border border-border/50 rounded-full px-2 py-0.5 bg-background/40">
                    {t('profile.donated', { amount: formatCurrency(Number(profile.donations_total)) })}
                  </p>
                )}
                {!isOwnProfile && isLoadingProfileRank && profile?.is_public && profile?.is_premium && (
                  <div className="text-xs font-light text-muted-foreground">{t('profile.fetching_rank')}</div>
                )}
                {!isOwnProfile && !isLoadingProfileRank && profileRank && (
                  <button
                    onClick={() => navigate("/social")}
                    className="text-xs font-light text-muted-foreground hover:text-accent hover:underline cursor-pointer"
                  >
                    {getRankDisplay(profileRank)}
                  </button>
                )}
              </div>

              {/* Followers/Following counts and Follow button - on its own line */}
              <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                {/* Followers/Following counts - show to all authenticated users (not blocked) */}
                {followCounts && (profile.is_public || followStatus?.isFollowing || isOwnProfile) && (
                  <div className="flex items-center gap-3 text-sm">
                    <button
                      onClick={() => {
                        setFollowersDialogTab("followers");
                        setShowFollowersDialog(true);
                      }}
                      className="flex items-center gap-1 hover:underline cursor-pointer"
                    >
                      <span className="font-semibold">{formatNumber(followCounts.followers)}</span>
                      <span className="text-muted-foreground">{t('profile.followers')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setFollowersDialogTab("following");
                        setShowFollowersDialog(true);
                      }}
                      className="flex items-center gap-1 hover:underline cursor-pointer"
                    >
                      <span className="font-semibold">{formatNumber(followCounts.following)}</span>
                      <span className="text-muted-foreground">{t('profile.following')}</span>
                    </button>
                  </div>
                )}

                {/* Counts only (no clickable link) if private and not following */}
                {!profile.is_public && !followStatus?.isFollowing && !isOwnProfile && followCounts && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="flex items-center gap-1">
                      <span className="font-semibold">{formatNumber(followCounts.followers)}</span>
                      <span className="text-muted-foreground">{t('profile.followers')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-semibold">{formatNumber(followCounts.following)}</span>
                      <span className="text-muted-foreground">{t('profile.following')}</span>
                    </div>
                  </div>
                )}

                {/* Follow/Unfollow button for other users' profiles */}
                {!isOwnProfile && currentUser && (
                  <>
                    {followStatus?.isFollowing ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => unfollowUser.mutate()}
                        disabled={unfollowUser.isPending}
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        {t('profile.following_button')}
                      </Button>
                    ) : followStatus?.hasRequested ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancelRequest.mutate()}
                        disabled={cancelRequest.isPending}
                      >
                        <UserMinus className="w-4 h-4 mr-1" />
                        {t('profile.requested')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => followUser.mutate()}
                        disabled={followUser.isPending}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        {t('profile.follow')}
                      </Button>
                    )}
                  </>
                )}
              </div>

              <div className="flex gap-2 mt-2">
                {!isMobile && (
                  <Button
                    onClick={() => {
                      window.open("https://gitfit.ai/showcase", "_blank", "noopener,noreferrer");
                    }}
                    variant="outline"
                    className="relative flex items-center gap-2 px-12 font-semibold border-2 gradient-glow-button overflow-visible"
                  >
                    <Activity className="w-4 h-4" />
                    {t('profile.create_free_account')}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    if (!currentUser) {
                      navigate("/auth");
                    } else {
                      navigate("/social");
                    }
                  }}
                  variant="ghost"
                  size="icon"
                >
                  <Users className="w-5 h-5" />
                </Button>
                {!isOwnProfile && hasAccess && currentUser && !isMobile && (
                  <Button
                    onClick={() => {
                      navigate(`/compare?mode=versus&opponent=${profile.id}`);
                    }}
                    variant="ghost"
                    size="icon"
                    title={t('profile.compare_graphs')}
                  >
                    <Activity className="w-5 h-5" />
                  </Button>
                )}
                <Button
                  onClick={() => {
                    if (!currentUser) {
                      navigate("/auth");
                    } else {
                      navigate("/settings");
                    }
                  }}
                  variant="ghost"
                  size="icon"
                >
                  <Settings className="w-5 h-5" />
                </Button>
                {!isOwnProfile && hasAccess && currentUser && isMobile && (
                  <Button
                    onClick={() => {
                      navigate(`/compare?mode=versus&opponent=${profile.id}`);
                    }}
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-1"
                  >
                    <Activity className="w-4 h-4" />
                    {t('profile.compare_you')}
                  </Button>
                )}
              </div>

              {/* App Store / Play Store buttons for anonymous visitors */}
              {!currentUser && (
                <div className="flex flex-row items-center gap-3 mt-3">
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackStoreClick("apple", "profile_view")}
                    className="flex-shrink-0 cursor-pointer rounded-xl transition-all duration-200 ease-out hover:scale-105 hover:brightness-110 hover:drop-shadow-[0_4px_20px_rgba(255,255,255,0.15)] active:scale-95 active:brightness-90"
                  >
                    <img
                      src="/Download_AppStore.png"
                      alt={t('showcase.download_app_store')}
                      className="h-10 sm:h-12 w-auto object-contain"
                    />
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackStoreClick("google", "profile_view")}
                    className="flex-shrink-0 cursor-pointer rounded-xl transition-all duration-200 ease-out hover:scale-105 hover:brightness-110 hover:drop-shadow-[0_4px_20px_rgba(255,255,255,0.15)] active:scale-95 active:brightness-90"
                  >
                    <img
                      src="/Download_PlayStore.png"
                      alt={t('showcase.download_play_store')}
                      className="h-10 sm:h-12 w-auto object-contain"
                    />
                  </a>
                </div>
              )}
              
              {/* Screenshot mode toggle - only for demo account @aaron */}
              {isOwnProfile && isDemoAccount(profile?.username) && (
                <div className="mt-3">
                  <Button
                    onClick={() => setShowDiceButtons(!showDiceButtons)}
                    variant={showDiceButtons ? "default" : "outline"}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Dices className="w-4 h-4" />
                    {showDiceButtons ? "Hide Randomizers" : "Screenshot Mode"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {!isOwnProfile && !hasAccess && (
            <Card className="p-6 mb-6 bg-muted/50">
              <div className="flex items-start gap-4">
                <Lock className="w-6 h-6 text-muted-foreground mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold mb-2">{t('profile.locked_title')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('profile.locked_desc')}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {hasAccess && (
            <div className="bg-card border border-border rounded-lg" style={{ padding: '18px' }}>
              <ContributionChart
                userId={profile.id}
                chartOnly
                showFilters={isOwnProfile}
                isPremium={profile.is_premium}
                showReactions={!isOwnProfile}
                currentViewerId={currentUser || undefined}
                showDiceButtons={showDiceButtons && isDemoAccount(profile?.username)}
              />
            </div>
          )}
        </header>

        {/* Profile Visibility Dialog */}
        <Dialog open={showVisibilityDialog} onOpenChange={setShowVisibilityDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('profile.make_public_title')}</DialogTitle>
              <DialogDescription>
                {t('profile.make_public_copy_desc')}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="flex items-center gap-3 p-4 border rounded-lg">
                {profile?.is_public ? (
                  <>
                    <Eye className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <Label className="font-semibold">{t('profile.public_profile')}</Label>
                      <p className="text-sm text-muted-foreground">{t('profile.visible_everyone')}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <Label className="font-semibold">{t('profile.private_profile')}</Label>
                      <p className="text-sm text-muted-foreground">{t('profile.visible_friends')}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <DialogFooter className="gap-3">
              <Button variant="outline" onClick={() => setShowVisibilityDialog(false)}>
                {t('profile.keep_private')}
              </Button>
              <Button onClick={() => updateVisibility.mutate(true)} disabled={updateVisibility.isPending}>
                {updateVisibility.isPending ? t('profile.updating') : t('profile.make_public')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Followers/Following Dialog */}
        <FollowersFollowingDialog
          open={showFollowersDialog}
          onOpenChange={setShowFollowersDialog}
          profileUserId={profile.id}
          initialTab={followersDialogTab}
        />
      </div>
    </div>
  );
};

export default Profile;
